/**
 * Chat Message Component
 * Individual message bubble with markdown rendering for bot messages
 *
 * Accessibility:
 * - Semantic article element for each message
 * - Proper time formatting with datetime attribute
 * - Respects reduced motion preferences
 * - Readable text size (16px base)
 */

import React, { useMemo, memo, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage as ChatMessageType, ChatSuggestion } from '../types/chat.types';
import { cn } from '../../../../lib/utils';
import { MessageActions } from './MessageActions';
import { ChatSuggestions } from './ChatSuggestions';
import { OffTopicIndicator } from './OffTopicIndicator';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';

interface ChatMessageProps {
  message: ChatMessageType;
  suggestions?: ChatSuggestion[];
  isOffTopic?: boolean;
  isStreaming?: boolean;
  onRetry?: (messageId: string) => void;
  onSuggestionClick?: (suggestion: ChatSuggestion) => Promise<void>;
  onFeedback?: (messageId: string, isPositive: boolean) => void;
  onRegenerate?: (messageId: string) => void;
  feedbackGiven?: 'positive' | 'negative' | null; // Current feedback state for this message
  className?: string;
}

/**
 * Static markdown components for bot messages
 * Optimized for performance - no animations on individual elements
 * Using CSS for styling, not Framer Motion
 */
const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <p className="mb-3 last:mb-0 leading-relaxed text-gray-800">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <strong className="font-semibold text-emerald-700">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <em className="italic text-gray-700">{children}</em>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }): React.JSX.Element => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-emerald-600 hover:text-emerald-700 underline underline-offset-2 transition-colors font-medium"
    >
      {children}
    </a>
  ),
  ul: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <ul className="list-disc list-outside mb-3 space-y-1 ml-5 text-gray-800">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <ol className="list-decimal list-outside mb-3 space-y-1 ml-5 text-gray-800">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <li className="leading-relaxed">{children}</li>
  ),
  code: ({ children, className }: { children?: React.ReactNode; className?: string }): React.JSX.Element => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded-sm text-sm font-mono">
        {children}
      </code>
    ) : (
      <code className={className}>{children}</code>
    );
  },
  pre: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <pre className="bg-gray-50 rounded-xl p-4 mb-3 text-sm font-mono border border-gray-100 overflow-x-auto">
      {children}
    </pre>
  ),
  h1: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <h1 className="text-xl font-bold mb-3 mt-4 first:mt-0 text-gray-900">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <h2 className="text-lg font-semibold mb-2 mt-3 first:mt-0 text-gray-900">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <h3 className="text-base font-semibold mb-2 mt-2 first:mt-0 text-gray-900">{children}</h3>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }): React.JSX.Element => (
    <blockquote className="border-l-3 border-emerald-400 pl-4 italic my-3 text-gray-600 bg-emerald-50/30 py-2 rounded-r">
      {children}
    </blockquote>
  ),
  hr: (): React.JSX.Element => <hr className="my-4 border-gray-200" />,
};

const ChatMessageComponent: React.FC<ChatMessageProps> = ({
  message,
  suggestions,
  isOffTopic,
  isStreaming: isStreamingProp,
  onRetry: _onRetry,
  onSuggestionClick,
  onFeedback,
  onRegenerate,
  feedbackGiven,
  className
}) => {
  // Use prop if provided, otherwise fallback to message.isStreaming
  const isMessageStreaming = isStreamingProp ?? message.isStreaming;
  const isUser = message.type === 'user';
  const isSystem = message.type === 'system';
  const isBot = message.type === 'bot';

  // Check if message has content (used in multiple places)
  const hasContent = message.content && message.content.trim().length > 0;

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS (Rules of Hooks)
  const [isHovered, setIsHovered] = useState(false);
  const [isTapped, setIsTapped] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const { prefersReducedMotion, tween } = useReducedMotion();

  // Check if message is long (more than 500 words)
  const wordCount = message.content.split(/\s+/).length;
  const isLong = wordCount > 500;

  // Handlers for hover/tap state
  const handleHoverStart = useCallback(() => {
    if (!isMobile) setIsHovered(true);
  }, [isMobile]);

  const handleHoverEnd = useCallback(() => {
    if (!isMobile) setIsHovered(false);
  }, [isMobile]);

  const handleTap = useCallback(() => {
    if (isMobile && isBot && !isMessageStreaming) {
      setIsTapped(prev => !prev);
    }
  }, [isMobile, isBot, isMessageStreaming]);

  // Memoize markdown rendering for bot messages
  // Handles edge case of empty content gracefully
  const renderedContent = useMemo(() => {
    if (isBot) {
      // If bot message is empty and not streaming, show placeholder
      if (!hasContent && !isMessageStreaming) {
        return (
          <div className="text-base text-gray-400 italic">
            Unable to load response. Please try again.
          </div>
        );
      }

      // If streaming with no content yet, show nothing (cursor will appear)
      if (!hasContent && isMessageStreaming) {
        return null;
      }

      try {
        return (
          <div className="prose prose-base max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        );
      } catch {
        return <div className="text-base">{message.content}</div>;
      }
    }
    // Plain text for user and system messages
    return message.content;
  }, [message.content, isBot, isMessageStreaming, hasContent]);

  // Format timestamp for accessibility with defensive handling
  // Guard against invalid/missing timestamps (0, undefined, null, NaN)
  const hasValidTimestamp = message.timestamp && message.timestamp > 0 && !isNaN(message.timestamp);
  const timestamp = hasValidTimestamp ? new Date(message.timestamp) : new Date();
  const formattedTime = hasValidTimestamp ? format(timestamp, 'HH:mm') : '--:--';
  const isoTime = timestamp.toISOString();

  // EARLY RETURN: Don't render empty streaming bot messages at all
  // The typing indicator already shows streaming status - empty bubbles with just cursor look broken
  // This prevents orphaned empty messages from showing the animated cursor
  // NOTE: This must be AFTER all hooks to comply with Rules of Hooks
  if (isBot && !hasContent && isMessageStreaming) {
    return null;
  }

  return (
    <motion.article
      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.97 }}
      animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
      transition={prefersReducedMotion ? { duration: 0 } : {
        ...tween,
        delay: isBot ? 0.03 : 0
      }}
      className={cn(
        'flex flex-col mb-5',
        'max-md:mb-4',
        isUser ? 'items-end' : 'items-start',
        className
      )}
      aria-label={`${message.type} message`}
    >
      <motion.div
        whileHover={prefersReducedMotion || isSystem || isMobile ? {} : { scale: 1.005 }}
        transition={{ duration: 0.15 }}
        onHoverStart={handleHoverStart}
        onHoverEnd={handleHoverEnd}
        onTap={handleTap}
        className={cn(
          // Width and padding - increased for better readability
          'max-w-[85%] rounded-2xl px-4 py-4',
          'max-md:max-w-[92%] max-md:px-4 max-md:py-3.5',
          'shadow-xs relative group',
          'wrap-break-word',
          // Desktop: Add bottom padding for actions inside bubble
          isBot && !isMessageStreaming && !isMobile && 'pb-14',
          // Message type styles
          isUser && 'bg-emerald-600 text-white',
          !isUser && !isSystem && 'bg-white text-gray-900 border border-gray-200/80',
          isSystem && 'bg-blue-50 border border-blue-200 text-blue-900'
        )}
      >
        {/* Desktop: Actions inside bubble */}
        {isBot && !isMessageStreaming && !isMobile && (
          <MessageActions
            messageId={message.id}
            content={message.content}
            isLong={isLong}
            onCopy={() => {}} // Copy is handled internally by MessageActions
            onRegenerate={onRegenerate ? () => onRegenerate(message.id) : undefined}
            onFeedback={onFeedback ? (isPositive: boolean) => onFeedback(message.id, isPositive) : undefined}
            feedbackGiven={feedbackGiven}
            className={cn(
              'transition-opacity duration-200',
              isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
          />
        )}

        {/* Message Content - 16px base for readability */}
        <div
          className={cn(
            'text-base leading-relaxed', // 16px for readability
            'wrap-break-word'
          )}
        >
          {renderedContent}

          {/* Streaming cursor */}
          {isMessageStreaming && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={prefersReducedMotion
                ? { opacity: 1 }
                : { opacity: [0.5, 1, 0.5] }
              }
              transition={prefersReducedMotion
                ? { duration: 0 }
                : { duration: 1.0, repeat: Infinity, ease: 'easeInOut' }
              }
              className="inline-block w-0.5 h-5 ml-1 bg-emerald-500 rounded-full"
              aria-hidden="true"
            />
          )}
        </div>

        {/* Timestamp and status */}
        {!isMessageStreaming && (
          <div className={cn(
            'flex items-center gap-2',
            isBot && !isMobile && 'absolute bottom-3 left-4',
            (isMobile || !isBot) && 'mt-2'
          )}>
            <time
              dateTime={isoTime}
              className={cn(
                'text-xs font-medium',
                isUser ? 'text-emerald-100' : isSystem ? 'text-blue-600/70' : 'text-gray-400'
              )}
            >
              {formattedTime}
            </time>
            {isBot && isOffTopic && (
              <OffTopicIndicator size="sm" />
            )}
          </div>
        )}
      </motion.div>

      {/* Mobile: Actions below message */}
      {isBot && !isMessageStreaming && isMobile && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{
            opacity: isTapped ? 1 : 0,
            y: isTapped ? 0 : 8
          }}
          transition={tween}
          className={cn(
            'mt-2 flex items-center justify-center',
            isTapped ? 'pointer-events-auto' : 'pointer-events-none'
          )}
        >
          <MessageActions
            messageId={message.id}
            content={message.content}
            isLong={isLong}
            onCopy={() => {}} // Copy is handled internally by MessageActions
            onRegenerate={onRegenerate ? () => onRegenerate(message.id) : undefined}
            onFeedback={onFeedback ? (isPositive: boolean) => onFeedback(message.id, isPositive) : undefined}
            feedbackGiven={feedbackGiven}
            className="opacity-100"
            isMobileFloating={true}
          />
        </motion.div>
      )}

      {/* Suggestions */}
      {isBot && !isMessageStreaming && suggestions && suggestions.length > 0 && onSuggestionClick && (
        <ChatSuggestions
          suggestions={suggestions}
          isOffTopic={isOffTopic}
          onSuggestionClick={onSuggestionClick}
          messageId={message.id}
        />
      )}
    </motion.article>
  );
};

// Memoize component to prevent unnecessary re-renders
export const ChatMessage = memo(ChatMessageComponent, (prevProps, nextProps) => {
  // Core message data
  const messageUnchanged = (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.type === nextProps.message.type
  );

  // Streaming state (from prop or message)
  const streamingUnchanged = (
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.message.isStreaming === nextProps.message.isStreaming
  );

  // Suggestions comparison (shallow)
  const suggestionsUnchanged = prevProps.suggestions === nextProps.suggestions;

  // Other props
  const otherPropsUnchanged = (
    prevProps.isOffTopic === nextProps.isOffTopic &&
    prevProps.onSuggestionClick === nextProps.onSuggestionClick &&
    prevProps.onRetry === nextProps.onRetry
  );

  return messageUnchanged && streamingUnchanged && suggestionsUnchanged && otherPropsUnchanged;
});

export default ChatMessage;
